import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { Button, Card, StatCard } from '../../ui';
import { ProcessorStatePanel, useProcessorStates } from '../../ProcessorStateBadge';

const STATUS_URL = '/ui/admin/processors/post-backlog/status';

interface DryRunRow {
  id: string; status: string; oldCategory?: string | null; newCategory?: string;
  newConfidence?: number | null; changed: boolean; belowThreshold?: boolean; error?: string;
}

export const PostBacklogPanel: React.FC = () => {
  const { apiRequest } = useAuth();
  const queryClient = useQueryClient();
  const states = useProcessorStates();

  // Unlike CAF Backlog, the old panel never synced this from the server
  // response (post-backlog/status doesn't return a batchSize field) — it's
  // purely local UI state, default 100, ported as-is.
  const [batchSize, setBatchSize] = useState(100);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dryRunResults, setDryRunResults] = useState<DryRunRow[] | null>(null);

  const statusQuery = useQuery({
    queryKey: ['post-backlog-status'],
    queryFn: () => apiRequest<any>({ url: STATUS_URL, method: 'GET' }),
  });
  const status = statusQuery.data;
  const refreshStatus = () => queryClient.invalidateQueries({ queryKey: ['post-backlog-status'] });

  const toggleProcessor = async (enable: boolean) => {
    setBusy(true);
    try {
      await apiRequest({ url: `/ui/admin/processors/post-backlog/${enable ? 'enable' : 'disable'}`, method: 'POST' });
      setMessage(`Post Backlog Processor ${enable ? 'enabled' : 'disabled'} successfully`);
      refreshStatus();
    } catch {
      setMessage(`Failed to ${enable ? 'enable' : 'disable'} processor`);
    } finally {
      setBusy(false);
    }
  };

  const runPostBacklogProcessor = async (customBatchSize?: number, processAll?: boolean) => {
    setBusy(true);
    setMessage(processAll ? 'Processing entire post backlog...' : 'Running post backlog processor...');
    try {
      const payload: Record<string, unknown> = { batchSize: customBatchSize ?? batchSize };
      if (processAll) payload.processAll = true;
      const res = await apiRequest<{ success: boolean; message?: string; stats?: any; results?: any }>({
        url: '/ui/admin/processors/post-backlog/run', method: 'POST', data: payload,
      });
      const stats = res.stats || res.results;
      if (res.success && stats) {
        let summary = `Processing completed: ${stats.totalProcessed} tasks processed, ${stats.totalSuccess} successful, ${stats.totalErrors} errors`;
        if (stats.categoryChanged > 0) summary += `, ${stats.categoryChanged} categories changed`;
        if (stats.automationsTriggered > 0) summary += `, ${stats.automationsTriggered} automations triggered`;
        setMessage(summary);
        refreshStatus();
      } else {
        setMessage(res.message || 'Failed to run processor');
      }
    } catch {
      setMessage('Failed to run post backlog processor');
    } finally {
      setBusy(false);
    }
  };

  const runPostBacklogDryRun = async (customBatchSize?: number) => {
    setBusy(true);
    setMessage('Running dry-run preview...');
    try {
      const res = await apiRequest<{ success: boolean; message?: string; preview: DryRunRow[]; totalTasks: number }>({
        url: '/ui/admin/processors/post-backlog/dry-run', method: 'POST', data: { batchSize: customBatchSize ?? 10 },
      });
      if (res.success) {
        setDryRunResults(res.preview);
        const changed = res.preview.filter((p) => p.changed).length;
        setMessage(`Dry-run complete: ${res.preview.length} tasks previewed, ${changed} would change category`);
      } else {
        setMessage(res.message || 'Dry-run failed');
      }
    } catch {
      setMessage('Failed to run dry-run');
    } finally {
      setBusy(false);
    }
  };

  const lastRun = status?.lastRun?.results;

  return (
    <Card title="Post Backlog" actions={
      <>
        <Button size="sm" variant="secondary" disabled={status?.enabled || busy} onClick={() => toggleProcessor(true)}>Enable Processor</Button>
        <Button size="sm" variant="ghost" disabled={!status?.enabled || busy} onClick={() => toggleProcessor(false)}>Disable Processor</Button>
      </>
    }>
      <p className="pc-desc">
        Reclassifies existing post tasks using improved AI classification. Processes both completed and pending tasks,
        flags category changes, and triggers automations for eligible pending tasks.
      </p>

      <ProcessorStatePanel processorId="post_backlog" state={states.post_backlog} />

      {status && (
        <dl className="pc-status">
          <dt>totalTasks</dt><dd>{status.totalTasks}</dd>
          <dt>pendingTasks</dt><dd>{status.pendingTasks}</dd>
          {status.lastRun?.time && (<><dt>lastRun</dt><dd>{new Date(status.lastRun.time).toLocaleString()}</dd></>)}
        </dl>
      )}

      {lastRun && (
        <div className="pc-stat-grid">
          <StatCard value={lastRun.totalProcessed} label="processed" />
          <StatCard value={lastRun.totalSuccess} label="successful" tone="success" />
          <StatCard value={lastRun.totalErrors} label="errors" tone={lastRun.totalErrors > 0 ? 'error' : 'muted'} />
          <StatCard value={lastRun.categoryChanged} label="categories changed" />
          <StatCard value={lastRun.automationsTriggered} label="automations triggered" />
          <StatCard value={lastRun.belowThresholdCount} label="below threshold" tone="muted" />
        </div>
      )}

      <div className="pc-config">
        <label>Batch Size
          <input type="number" min={1} max={500} aria-label="Batch size" value={batchSize}
                 disabled={status?.enabled}
                 onChange={(e) => setBatchSize(parseInt(e.target.value, 10) || 100)} />
        </label>
      </div>

      <div className="pc-actions">
        <Button variant="primary" disabled={busy} onClick={() => runPostBacklogProcessor()}>{busy ? 'Running...' : 'Run Now'}</Button>
        <Button variant="secondary" disabled={busy} onClick={() => runPostBacklogProcessor(10)}>Test Run (10 tasks)</Button>
        <Button variant="secondary" disabled={busy} onClick={() => runPostBacklogProcessor(1)}>Single Task Test</Button>
        <Button variant="secondary" disabled={busy} onClick={() => runPostBacklogDryRun(20)}>Dry Run Preview (20)</Button>
        <Button variant="danger" disabled={busy} onClick={() => runPostBacklogProcessor(undefined, true)}>Process Entire Backlog</Button>
        <Button variant="secondary" disabled={busy} onClick={refreshStatus}>Refresh Status</Button>
      </div>

      {dryRunResults && dryRunResults.length > 0 && (
        <div className="pc-haiku-panel">
          <strong>Dry Run Preview Results</strong>
          <p className="pc-desc">{dryRunResults.filter((r) => r.changed).length} of {dryRunResults.length} tasks would change category</p>
          <div className="pc-table-wrap">
            <table className="pc-table">
              <thead>
                <tr><th>Task ID</th><th>Status</th><th>Old Category</th><th>New Category</th><th>Confidence</th><th>Changed</th></tr>
              </thead>
              <tbody>
                {dryRunResults.map((r, idx) => (
                  <tr key={idx} className={[r.changed && 'pc-row--changed', r.error && 'pc-row--error'].filter(Boolean).join(' ') || undefined}>
                    <td>{r.id?.substring(0, 8)}...</td>
                    <td>{r.status}</td>
                    <td>{r.oldCategory || '-'}</td>
                    <td>{r.error ? r.error : r.newCategory}{r.belowThreshold && ' (low)'}</td>
                    <td>{r.newConfidence != null ? `${(r.newConfidence * 100).toFixed(0)}%` : '-'}</td>
                    <td>{r.changed ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setDryRunResults(null)}>Dismiss</Button>
        </div>
      )}

      {message && <p className="pc-desc">{message}</p>}
    </Card>
  );
};
